// Path: /components/Layout/Navbar/Navbar.jsx
"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import Dropdown from '/components/Dropdowns/NavbarDropdown'; // Your existing dropdown
import ProductAuthControl from '/components/Auth/ProductAuthControl';
import styles from './Navbar.module.css';

const Navbar = ({ hideHeader, variant = 'shuffledb', productHref = '/TrackDat' }) => {
  console.log("Navbar rendered");
  const { data: session } = useSession();
  const product = ['capdat','postdat','trackdat','editdat'].includes(variant) ? variant : '';
  const isProduct = Boolean(product);
  const productName = product === 'capdat' ? 'CapDat' : product === 'postdat' ? 'PostDat' : product === 'editdat' ? 'EditDat' : 'TrackDat';
  const productLinks = [
    { label: productName, href: productHref },
    { label: 'ShuffleDB', href: '/' },
    { label: 'ShuffleMaps', href: '/ShuffleMaps' },
    ...(product !== 'capdat' ? [{ label: 'CapDat', href: '/CapDat' }] : []),
    ...(product !== 'trackdat' ? [{ label: 'TrackDat', href: '/TrackDat' }] : []),
    ...(product !== 'editdat' && product !== 'trackdat' ? [{ label: 'EditDat', href: '/EditDat' }] : []),
    ...(product !== 'postdat' ? [{ label: 'PostDat', href: '/PostDat' }] : []),
  ];
  const missionLinks = [
    { label: 'My Mission History', href: '/Profile#mission-history' },
    ...(session?.user?.isAdmin === true || session?.user?.permissions?.includes('missions.manage') ? [
      { label: 'Create a Mission', href: '/MissionCreator' },
    ] : []),
    ...(session?.user?.isAdmin === true || session?.user?.permissions?.includes('missions.review') ? [
      { label: 'Review Submissions', href: '/MissionReview' },
    ] : []),
  ];

  // Inline style to force the nav element to move to the top when hideHeader is true.
  // This ensures that when the header is hidden, the navbar slides to the top.
  const navStyle = hideHeader ? { top: 0 } : {};
  const navRef = useRef(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;

    let animationFrame = 0;
    const updateNavOffset = () => {
      const computedStyle = getComputedStyle(nav);
      const top = Math.max(0, parseFloat(computedStyle.top) || 0);
      const height = nav.getBoundingClientRect().height || 40;
      document.documentElement.style.setProperty('--nav-offset', `${Math.round(top + height)}px`);
    };
    const scheduleUpdate = () => {
      if (animationFrame) return;
      animationFrame = requestAnimationFrame(() => {
        animationFrame = 0;
        updateNavOffset();
      });
    };

    updateNavOffset();
    window.addEventListener('resize', scheduleUpdate, { passive: true });
    nav.addEventListener('transitionend', scheduleUpdate);
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(nav, { attributes: true, attributeFilter: ['class', 'style'] });

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      window.removeEventListener('resize', scheduleUpdate);
      nav.removeEventListener('transitionend', scheduleUpdate);
      observer.disconnect();
      document.documentElement.style.removeProperty('--nav-offset');
    };
  }, [hideHeader]);

  return (
    <div className={styles.navbarContent}>
      <nav ref={navRef} className={`${styles.nav} ${isProduct ? styles.trackDatNav : ''} ${hideHeader ? styles.stickyNav : ''}`} style={navStyle}>
        {isProduct ? (
          <>
            <div className={styles.trackDatNavLinks}>
              <Link className={`${styles.navLink} ${styles.activeNavLink}`} href={productHref}>
                {productName}
              </Link>
              <Link className={styles.navLink} href="/">ShuffleDB</Link>
              <Link className={styles.navLink} href="/ShuffleMaps">ShuffleMaps</Link>
              {product!=='capdat'&&<Link className={styles.navLink} href="/CapDat">CapDat</Link>}
              {product!=='trackdat'&&<Link className={styles.navLink} href="/TrackDat">TrackDat</Link>}
              {product !== 'editdat' && product !== 'trackdat' && (
                <Link className={styles.navLink} href="/EditDat">EditDat</Link>
              )}
              {product!=='postdat'&&<Link className={styles.navLink} href="/PostDat">PostDat</Link>}
              <Dropdown
                className={styles.productNavDropdown}
                items={productLinks}
                label={productName}
                cycle={false}
              />
            </div>
            <div className={styles.trackDatNavAuth}>
              <ProductAuthControl
                product={product}
                callbackUrl={productHref}
                navbar
              />
            </div>
          </>
        ) : (
          <>
            {/* Include existing Dropdown */}
            <Dropdown />

            {/* Additional Links */}
            <Dropdown
              className={styles.missionNavDropdown}
              items={missionLinks}
              label="Missions"
              cycle={false}
            />
            <Link className={`${styles.navLink} ${styles.ecosystemSpacing}`} href="/Mindmap">Shuffle MindMap</Link>
            <Link className={`${styles.navLink} ${styles.ecosystemLink}`} href="/ShuffleMaps">ShuffleMaps</Link>
            <Link className={`${styles.navLink} ${styles.ecosystemLink}`} href="/TrackDat">TrackDat</Link>
            <Link className={`${styles.navLink} ${styles.ecosystemLink}`} href="/CapDat">CapDat</Link>
            <Link className={`${styles.navLink} ${styles.ecosystemLink}`} href="/PostDat">PostDat</Link>

            <div className={styles.shuffleDbNavAuth}>
              <ProductAuthControl
                product="shuffledb"
                callbackUrl="/"
                navbar
              />
            </div>
          </>
        )}
      </nav>
    </div>
  );
};

export default Navbar;
